import React, { useEffect, useMemo, useState } from "react";

import { Button } from "semantic-ui-react";

import { makeStyles } from "@mui/styles";
import moment from "moment";
import { useIntl } from "react-intl";

import ModInfo from "react-lib/apps/common/ModInfo";

import { getAddressDetail } from "react-lib/apps/MSG/StaffChatInterface";

import { formatDatetime, formatLocalizedDateTime } from "react-lib/utils/dateUtils";
import formatComma from "react-lib/utils/formatComma";

type ChatAppointmentContentProps = {
  onEvent: (e: any) => any;
  author: any;
  contentPayload: any;
  history?: any;
  userId?: number | string;
};

const useStyles = makeStyles(() => ({
  btnSubmit: {
    "&.ui.button": {
      backgroundColor: "var(--primary-theme-color)",
      borderRadius: "6px",
      color: "white",
      marginTop: "1rem",
      minWidth: "160px",
      padding: "9px 12px",
    },
  },
  container: {
    alignItems: "center",
    color: "rgba(60, 60, 60, 1)",
    display: "flex",
    justifyContent: "center",
    padding: "0.75rem 1rem",
  },
  contentWrapper: {
    background: "white",
    borderRadius: "0 0 1.5em 1.5em",
    padding: "1rem 1rem 1.5rem",
  },
  label: {
    fontSize: "14px",
    fontWeight: "bold",
    marginLeft: "0.5rem",
  },
  payment: {
    color: "rgba(255, 0, 0, 1)",
    fontSize: "13px",
    margin: "-0.25rem 0",
  },
  paymentStatus: {
    borderRadius: "5px",
    margin: "0.5rem 0 0",
    padding: "3.5px 7px",
    width: "fit-content",
  },
}));

const IMAGES = {
  female: "/images/common/icon_placeholder_female.svg",
  male: "/images/common/icon_placeholder_male.svg",
};

const ChatAppointmentContent = (props: ChatAppointmentContentProps) => {
  const classes = useStyles();
  const { locale } = useIntl();

  const [patientAppDetail, setPatientAppDetail] = useState<Record<string, any> | null>(null);
  const [openModInfo, setOpenModInfo] = useState<boolean>(false);

  useEffect(() => {
    const getPatientAppDetail = async () => {
      const id = props.contentPayload?.patient_appointment_id;

      const [data] = await props.onEvent({
        message: "GetPatientAppointmentDetail",
        params: { id },
      });

      setPatientAppDetail(data);
    };

    void getPatientAppDetail();
  }, [props.contentPayload?.patient_appointment_id]);

  const datetime = useMemo(
    () => formatLocalizedDateTime(patientAppDetail?.estimated_at_iso, locale),
    [locale, patientAppDetail?.estimated_at_iso]
  );

  const statusConfig = useMemo(() => {
    const status = patientAppDetail?.status;
    const isBilled = patientAppDetail?.extra?.payment_status === "BILLED";

    if (!status || !patientAppDetail.estimated_at_iso) {
      return { button: "", color: "", label: "", status: "" };
    }

    if (isBilled) {
      return { button: "ชำระเงิน", color: "", label: "", status: "BILLED" };
    }

    if (status === 2) {
      return {
        button: "",
        color: "rgba(36, 197, 36, 0.3)",
        label: "ยืนยันนัดหมายแล้ว",
        status: "CONFIRMED",
      };
    }

    if (!props.contentPayload?.update) {
      return {
        button: "ตรวจสอบข้อมูลนัดหมาย",
        color: "rgba(19, 221, 19, 0.36)",
        label: "", // "ชำระเงินแล้วเรียบร้อย",
        status: "DETAILED",
      };
    }

    return {
      button: "ยืนยันนัดหมาย",
      color: "rgba(255, 187, 123, 0.86)",
      label: "กรุณายืนยันนัดหมาย",
      status: "REQUESTED",
    };
  }, [patientAppDetail?.status, props.contentPayload?.update]);

  const profileImage = useMemo(() => {
    const placeholderImage = patientAppDetail?.doctor_gender === "F" ? IMAGES.female : IMAGES.male;

    return (patientAppDetail?.doctor_image || placeholderImage) as string;
  }, [patientAppDetail?.doctor_gender, patientAppDetail?.doctor_image]);

  const addressDetail = useMemo(() => getAddressDetail(patientAppDetail ?? {}), [patientAppDetail]);

  const handleSubmit = async () => {
    if (!props.history) {
      if (statusConfig.status === "DETAILED") {
        setOpenModInfo(true);
      }

      return;
    }

    props.onEvent({
      message: "HandleSelectAppointment",
      params: {
        data: patientAppDetail,
        history: props.history,
        isPaymentReadOnly: statusConfig.status !== "BILLED",
      },
    });
  };

  const handleCloseModInfo = () => {
    setOpenModInfo(false);
  };

  return (
    <div
      className="chat-appointment-content"
      style={{
        backgroundColor: "rgba(255, 199, 194, 0.52)",
        margin:
          props.author?.id === Number(props.userId)
            ? "-0.75rem -1.25rem -0.8rem -1.25rem"
            : "-0.8rem -1.15rem -0.8rem -1.2rem",
        minWidth: "17rem",
      }}
    >
      <div className={classes.container}>
        <img
          style={{
            width: "25px",
          }}
          alt="appointment.icon"
          src="/images/common/icon_make_appointment.png"
        />
        <label className={classes.label}>นัดหมาย</label>
      </div>
      <div className={classes.contentWrapper}>
        <div>{props.contentPayload?.doctor_name}</div>
        <div>{datetime}</div>
        <div
          style={{
            margin: "0.5rem 0 0.75rem",
          }}
        >
          {patientAppDetail?.extra.service?.name || ""}
        </div>

        {!!statusConfig.label && (
          <div className={classes.paymentStatus} style={{ backgroundColor: statusConfig.color }}>
            {statusConfig.label}
          </div>
        )}

        {statusConfig.status === "BILLED" && (
          <div className={classes.payment}>
            <div>กรุณาชำระเงินจำนวน {formatComma(patientAppDetail?.extra.service?.price)}</div>
            <div>
              ภายใน {formatDatetime(moment(patientAppDetail?.created).clone().add(24, "hours"))}
            </div>
            <div>หากไม่ชำระรายการนัดหมายของคุณจะถูกยกเลิกทันที</div>
          </div>
        )}

        {!!statusConfig.button && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button className={classes.btnSubmit} size="small" onClick={handleSubmit}>
              {statusConfig.button}
            </Button>
          </div>
        )}
      </div>

      <ModInfo
        style={{ minWidth: "30rem" }}
        backgroundColor="var(--primary-theme-color)"
        btnText="ปิดหน้าจอ"
        open={openModInfo}
        titleName="รายละเอียดการนัดหมาย"
        onApprove={handleCloseModInfo}
        onClose={handleCloseModInfo}
      >
        <div style={{ margin: "-1rem 0 0.75rem", textAlign: "left" }}>
          <div style={{ alignItems: "center", display: "flex", marginBottom: "1.25rem" }}>
            <div
              style={{
                borderRadius: "10px",
                height: "47.5px",
                marginRight: "1rem",
                width: "47.5px",
              }}
            >
              <img style={{ height: "47.5px", width: "47.5px" }} alt="user" src={profileImage} />
            </div>
            <div>
              <div
                style={{
                  color: "rgba(60, 60, 60, 1)",
                  fontSize: "16px",
                  fontWeight: "bold",
                  marginBottom: "0.5rem",
                }}
              >
                {patientAppDetail?.doctor_full_name || "-"}
              </div>
              {/* <div>เลขที่ใบประกอบวิชาชีพ {patientAppDetail?.doctor_certificate_no || "-"}</div> */}
            </div>
          </div>

          <div style={{ display: "flex", marginBottom: "0.75rem" }}>
            <div style={{ color: "rgba(60, 60, 60, 1)", minWidth: "150px" }}>วันเวลาที่นัดหมาย</div>
            <div>{datetime}</div>
          </div>

          <div style={{ display: "flex", marginBottom: "0.75rem" }}>
            <div style={{ color: "rgba(60, 60, 60, 1)", minWidth: "150px" }}>สถานที่รับบริการ</div>
            <div style={{}}>{addressDetail.name}</div>
          </div>

          <div style={{ marginBottom: "0.25rem" }}>
            <div style={{ minWidth: "150px" }}>วัตถุประสงค์ในการนัดหมาย</div>
          </div>
          <div style={{ lineHeight: "1.5", textIndent: "1rem" }}>
            {patientAppDetail?.order_note}
          </div>
        </div>
      </ModInfo>
    </div>
  );
};

export default ChatAppointmentContent;
